/**
 * Edit a project — `GET/PUT /projects/{id}`.
 *
 * Uses the CoOrdio form-page-pattern: PageFormLayout with navGroups sidebar,
 * FormField per input wrapping Rhf*Field bridges, FormSection for card grouping,
 * and the `ui-form-choice-field` pattern for toggles.
 *
 * The Settings tab hosts `ProjectSettingsForm`, which manages its own state and
 * save action independently from the Details form.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { ArrowLeft, FileText, Settings } from 'lucide-react';

import {
  Button,
  FormErrors,
  FormField,
  RhfInputField,
  RhfTextareaField,
  ShellHeader,
  Switch,
  rhfFieldErrorMessages,
} from '@coordio/ui';
import {
  EmptyState,
  FormSection,
  InlineErrorPanel,
  PageContainer,
  StatusBadge,
} from '@coordio/ui/layout';

import { PageFormLayout } from '../components/layouts/PageFormLayout';
import { ProjectSettingsForm } from '../components/projects/ProjectSettingsForm';
import { projectService } from '../services/projectService';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';

import type { ProjectDto, UpdateProjectRequest } from '../types/project';

import './ProjectEditPage.scss';

/** Slugs are URL path segments, so the shape is enforced rather than merely suggested. */
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const projectSchema = z.object({
  name: z.string().trim().min(1, 'Name is required.').max(200, 'Name must be 200 characters or fewer.'),
  slug: z
    .string()
    .trim()
    .min(1, 'Slug is required.')
    .max(100, 'Slug must be 100 characters or fewer.')
    .regex(SLUG_PATTERN, 'Use lowercase letters, numbers and single hyphens (e.g. claims-triage).'),
  description: z.string().trim().max(1000, 'Description must be 1000 characters or fewer.').optional(),
  isActive: z.boolean(),
});

type ProjectFormValues = z.infer<typeof projectSchema>;

/** Field name → the label shown above its input, so the error summary matches the form. */
const FIELD_LABELS: Partial<Record<keyof ProjectFormValues, string>> = {
  name: 'Name',
  slug: 'Slug',
  description: 'Description',
  isActive: 'Active',
};

/**
 * `rhfFieldErrorMessages` includes the `root` key, and `FormErrors` renders `rootError` in its own
 * slot — passing the raw map alongside `rootError` prints a server failure twice, once as a banner
 * and once as a phantom field named "root".
 */
function fieldErrorsWithoutRoot(messages: Record<string, string | undefined>) {
  const { root: _root, ...fieldErrors } = messages;
  return fieldErrors;
}

function toFormValues(project: ProjectDto | null): ProjectFormValues {
  return {
    name: project?.name ?? '',
    slug: project?.slug ?? '',
    description: project?.description ?? '',
    isActive: project?.isActive ?? true,
  };
}

export const ProjectEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const currentTenant = useContextStore((s) => s.currentTenant);
  const refreshSwitcherProjects = useContextStore((s) => s.loadProjects);
  const { addNotification } = useNotificationStore();

  const [project, setProject] = useState<ProjectDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ProjectFormValues>({
    resolver: zodResolver(projectSchema),
    defaultValues: toFormValues(null),
    mode: 'onBlur',
  });

  const tenantId = currentTenant?.id;

  const loadProject = useCallback(async () => {
    if (!id || !tenantId) return;

    setLoading(true);
    setLoadError(null);
    try {
      const data = await projectService.getProject(id, tenantId);
      setProject(data);
      reset(toFormValues(data));
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to load project.';
      setLoadError(message);
      setProject(null);
    } finally {
      setLoading(false);
    }
  }, [id, tenantId, reset]);

  useEffect(() => {
    void loadProject();
  }, [loadProject]);

  const onSubmit = handleSubmit(async (values) => {
    if (!id || !tenantId) return;

    try {
      const payload: UpdateProjectRequest = {
        name: values.name,
        slug: values.slug,
        description: values.description || undefined,
        isActive: values.isActive,
      };
      const updated = await projectService.updateProject(id, tenantId, payload);
      setProject(updated);
      reset(toFormValues(updated));
      addNotification('Project updated successfully', 'success');
      void refreshSwitcherProjects(tenantId);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to update project.';
      setError('root', { message });
      addNotification(message, 'error');
    }
  });

  const backButton = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="gap-2"
      data-testid="back-project-btn"
      onClick={() => navigate(ROUTES.PROJECTS)}
    >
      <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
      Projects
    </Button>
  );

  /* ── Guard: no workspace selected ─────────────────────────────────────── */
  if (!tenantId) {
    return (
      <div className="project-edit" data-testid="project-form-page">
        <ShellHeader data-testid="project-header" title="Edit project" description="Choose a workspace before editing a project." actions={backButton} />
        <PageContainer variant="narrow">
          <EmptyState
            title="No workspace selected"
            description="Choose a workspace from the user menu before editing a project."
          />
        </PageContainer>
      </div>
    );
  }

  /* ── Guard: loading ───────────────────────────────────────────────────── */
  if (loading) {
    return (
      <PageFormLayout
        title="Edit project"
        subtitle="Loading project details…"
        data-testid="project-form-page"
        loading
        backTo={{ label: 'Projects', href: ROUTES.PROJECTS }}
        actions={{ onSave: () => {}, onCancel: () => navigate(ROUTES.PROJECTS) }}
      />
    );
  }

  /* ── Guard: load error ────────────────────────────────────────────────── */
  if (loadError) {
    return (
      <div className="project-edit" data-testid="project-form-page">
        <ShellHeader data-testid="project-header" title="Edit project" description="Unable to load this project." actions={backButton} />
        <PageContainer variant="narrow">
          <InlineErrorPanel
            data-testid="project-error-panel"
            title="Unable to load project"
            message={loadError}
            action={
              <Button type="button" variant="outline" size="sm" onClick={() => void loadProject()}>
                Try again
              </Button>
            }
          />
        </PageContainer>
      </div>
    );
  }

  /* ── Guard: project not found ─────────────────────────────────────────── */
  if (!project) {
    return (
      <div className="project-edit" data-testid="project-form-page">
        <ShellHeader data-testid="project-header" title="Edit project" description="Project not found." actions={backButton} />
        <PageContainer variant="narrow">
          <EmptyState
            title="Project not found"
            description="It may have been deleted, or belong to another workspace."
            action={
              <Button type="button" variant="outline" size="sm" onClick={() => navigate(ROUTES.PROJECTS)}>
                Back to projects
              </Button>
            }
          />
        </PageContainer>
      </div>
    );
  }

  /* ── Main form ────────────────────────────────────────────────────────── */
  return (
    <PageFormLayout
      title={project.name}
      subtitle={project.slug}
      data-testid="project-form-page"
      headerActions={<StatusBadge status={project.isActive ? 'Active' : 'Inactive'} />}
      backTo={{ label: 'Projects', href: ROUTES.PROJECTS }}
      actions={{
        onSave: onSubmit,
        onCancel: () => navigate(ROUTES.PROJECTS),
        saveLabel: 'Save changes',
        saving: isSubmitting,
      }}
      navGroups={[
        {
          title: 'Profile',
          items: [
            { id: 'details', label: 'Details', icon: <FileText size={14} /> },
          ],
        },
        {
          title: 'Configuration',
          items: [
            { id: 'settings', label: 'Settings', icon: <Settings size={14} /> },
          ],
        },
      ]}
      sections={[
        {
          id: 'details',
          title: 'Details',
          description: 'Name, slug and description as they appear across Cortex.',
          icon: <FileText size={16} />,
          content: (
            <>
              <FormErrors
                rootError={errors.root?.message}
                fieldErrors={fieldErrorsWithoutRoot(rhfFieldErrorMessages(errors))}
                fieldLabels={FIELD_LABELS}
                autoNavigateToFirstError
              />

              <FormSection
                data-testid="project-section-details"
                title="Project details"
                description="Name, slug and description as they appear across Cortex."
              >
                <div className="grid gap-5 items-end sm:grid-cols-2">
                  <FormField label="Name" required error={errors.name?.message} data-testid="project-name">
                    <RhfInputField control={control} name="name" placeholder="Claims triage" />
                  </FormField>

                  <FormField
                    label="Slug"
                    required
                    description="URL-friendly identifier. Changing it breaks existing links to this project."
                    error={errors.slug?.message}
                    data-testid="project-slug"
                  >
                    <RhfInputField control={control} name="slug" placeholder="claims-triage" />
                  </FormField>
                </div>

                <div className="mt-5">
                  <FormField label="Description" error={errors.description?.message} data-testid="project-description">
                    <RhfTextareaField control={control} name="description" rows={3} />
                  </FormField>
                </div>

                <div className="mt-5">
                  <div className="ui-form-choice-field">
                    <div className="ui-form-choice-field__row">
                      <div className="ui-form-choice-field__control">
                        <Controller
                          control={control}
                          name="isActive"
                          render={({ field }) => (
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              data-testid="project-isActive-switch"
                            />
                          )}
                        />
                      </div>
                      <div className="ui-form-choice-field__label-cell">
                        <span className="ui-form-choice-field__label">Active</span>
                      </div>
                    </div>
                  </div>
                </div>
              </FormSection>
            </>
          ),
        },
        {
          id: 'settings',
          title: 'Settings',
          description: 'Resource limits, budgets and IP fencing for this project.',
          icon: <Settings size={16} />,
          content: (
            <ProjectSettingsForm projectId={project.id} />
          ),
        },
      ]}
    />
  );
};
